const API_ORIGIN = (typeof import.meta !== 'undefined' && import.meta.env && import.meta.env.VITE_API_ORIGIN) || '';
const API_BASE = (typeof import.meta !== 'undefined' && import.meta.env && import.meta.env.VITE_API_BASE) || '/api/v1';
export const API_ROOT = `${API_ORIGIN || ''}${API_BASE || ''}`;

let authInFlight = null;

function nowMs() {
  try { return Date.now(); } catch { return 0; }
}

async function ensureTmaScript() {
  try {
    if (window?.Telegram?.WebApp) return;
  } catch {}
  await new Promise((resolve) => {
    try {
      const existing = document.querySelector('script[src="https://telegram.org/js/telegram-web-app.js"]');
      if (existing) {
        if (window?.Telegram?.WebApp) return resolve();
        existing.addEventListener('load', () => resolve());
        setTimeout(resolve, 1500);
        return;
      }
      const s = document.createElement('script');
      s.src = 'https://telegram.org/js/telegram-web-app.js';
      s.async = true;
      s.onload = () => resolve();
      s.onerror = () => resolve();
      document.head.appendChild(s);
      setTimeout(resolve, 1500);
    } catch { resolve(); }
  });
}

function getTelegramInitData() {
  try {
    const tg = window?.Telegram?.WebApp;
    return tg?.initData || '';
  } catch {
    return '';
  }
}

function getStoredAccessToken() {
  try {
    return (localStorage.getItem('auth_access_token') || window.__authAccessToken || '').trim();
  } catch {
    return '';
  }
}

function getStoredObtainedAt() {
  try {
    const v = localStorage.getItem('auth_obtained_at') || '';
    const n = parseInt(String(v || ''), 10);
    return Number.isFinite(n) ? n : 0;
  } catch {
    return 0;
  }
}

function getStoredTokenType() {
  try {
    return (localStorage.getItem('auth_token_type') || window.__authTokenType || 'Bearer').trim() || 'Bearer';
  } catch {
    return 'Bearer';
  }
}

function getDevBearer() {
  try {
    return (typeof import.meta !== 'undefined' && import.meta.env && import.meta.env.VITE_DEV_BEARER) || '';
  } catch {
    return '';
  }
}

function isTokenFresh() {
  const tok = getStoredAccessToken();
  if (!tok) return false;
  const obtainedAt = getStoredObtainedAt();
  if (!obtainedAt) return false;
  const ttlMs = 30 * 60 * 1000;
  const safetyMs = 60 * 1000;
  const age = nowMs() - obtainedAt;
  return age >= 0 && age < (ttlMs - safetyMs);
}

export function clearAuthToken() {
  try {
    localStorage.removeItem('auth_access_token');
    localStorage.removeItem('auth_token_type');
    localStorage.removeItem('auth_obtained_at');
  } catch { }
  try {
    delete window.__authAccessToken;
    delete window.__authTokenType;
  } catch { }
}

async function telegramLogin() {
  const initData = getTelegramInitData();
  if (!initData) return null;
  let hash = '';
  try {
    const params = new URLSearchParams(initData);
    hash = params.get('hash') || '';
  } catch { }
  if (!hash) return null;

  const res = await fetch(`${API_ROOT}/auth/telegram-login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ initData, hash }),
  });
  if (!res.ok) {
    throw new Error(`API error ${res.status}`);
  }
  const ct = res.headers.get('content-type') || '';
  if (ct.includes('application/json')) return await res.json();
  return null;
}

export async function ensureAuthToken({ force = false } = {}) {
  try {
    try { await ensureTmaScript(); } catch {}
    const dev = getDevBearer();
    const initData = getTelegramInitData();
    if (!initData) {
      if (dev) {
        try {
          localStorage.setItem('auth_access_token', dev);
          localStorage.setItem('auth_token_type', 'Bearer');
          localStorage.setItem('auth_obtained_at', String(nowMs()));
          window.__authAccessToken = dev;
          window.__authTokenType = 'Bearer';
        } catch { }
      }
      return;
    }

    if (!force && isTokenFresh()) return;
    if (authInFlight) return await authInFlight;

    authInFlight = (async () => {
      const data = await telegramLogin();
      const token = data?.access_token || '';
      const type = data?.token_type || 'Bearer';
      if (token) {
        try {
          localStorage.setItem('auth_access_token', token);
          localStorage.setItem('auth_token_type', type || 'Bearer');
          localStorage.setItem('auth_obtained_at', String(nowMs()));
          window.__authAccessToken = token;
          window.__authTokenType = type || 'Bearer';
        } catch { }
      }
    })();

    return await authInFlight;
  } finally {
    authInFlight = null;
  }
}

function shouldForceBearer() {
  try {
    const v = (typeof import.meta !== 'undefined' && import.meta.env && import.meta.env.VITE_FORCE_BEARER) || '';
    return String(v).trim() !== '' && String(v).trim() !== '0' && String(v).toLowerCase() !== 'false';
  } catch {
    return false;
  }
}

export function authHeaders(extra = {}) {
  const headers = { ...extra };
  const tok = getStoredAccessToken();
  const typ = getStoredTokenType();
  if (tok) {
    headers['Authorization'] = `${typ} ${tok}`;
    return headers;
  }
  const force = shouldForceBearer();
  const dev = getDevBearer();
  const initData = getTelegramInitData();
  if (initData) {
    headers['Authorization'] = `tma ${initData}`;
  } else if (dev) {
    headers['Authorization'] = `Bearer ${dev}`;
  }
  return headers;
}

export async function asyncAuthHeaders(extra = {}) {
  try { await ensureAuthToken(); } catch { }
  return authHeaders(extra);
}

export function quranDataUrl(path) {
  const clean = String(path || '').replace(/^\/+/, '');
  return `${API_ROOT}/quran-data/${clean}`;
}

export async function qfetch(path, options = {}) {
  const p = String(path || '');
  const cleanPath = p.includes('?') ? p.split('?')[0] : p;
  const url = quranDataUrl(cleanPath);
  const headers = await asyncAuthHeaders(options.headers || {});
  return fetch(url, { ...options, headers });
}
